// تاریخچه ارتباط (AP-ENG-02؛ US-ENG-023؛ AC-ENG-023.1): خط زمانی یکپارچه سرنخ یا مراجعه‌کننده — تماس‌های CHN، پیام‌ها (MSG و پیام سریع)، مشاوره‌ها، برآوردها،
// پیگیری‌های انجام‌شده، فرم وب و نظرسنجی — فقط با نوع و نتیجه (هیچ متن آزاد).
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { unwrap, type EngFollowUpSubjectKind, type EngHistoryItemView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation, type TFunction } from '@phoenix/i18n';
import { EmptyState, ErrorState, faDigits, JalaliDate, LoadingState } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { engKeys } from './common';

/** برچسب جزئیات هر نوع تعامل (کد نتیجه ← متن ترجمه‌شده). */
export function historyDetail(item: EngHistoryItemView, t: TFunction): string | null {
  const detail = item.detail;
  if (!detail) {
    return null;
  }
  switch (item.kind) {
    case 'consultation':
      return t(`eng:consultation_outcome.${detail}`);
    case 'followup':
      return t(`eng:followup_outcome.${detail}`);
    case 'quote':
      return t(`eng:quote_status.${detail}`);
    case 'call':
      return t(`chn:calls.outcome.${detail}`);
    case 'survey_response':
      return t('eng:history.nps', { score: faDigits(detail) });
    case 'web_form': {
      const [form, source] = detail.split(':');
      return t('eng:history.web_form_detail', {
        form: t(`eng:history.form_type.${form ?? ''}`, { defaultValue: form }),
        source: source ?? '',
      });
    }
    default:
      return detail;
  }
}

export function CommunicationHistory({
  subjectKind,
  subjectId,
}: {
  subjectKind: NonNullable<EngFollowUpSubjectKind>;
  subjectId: string;
}) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const history = useQuery({
    queryKey: [...engKeys.all(session?.tenantId), 'history', subjectKind, subjectId],
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/engagement/history', { params: { query: { subjectKind, subjectId } } })),
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="communication-history">
      <Typography variant="h3">{t('eng:history.title')}</Typography>
      {history.isPending && <LoadingState rows={3} />}
      {history.error && <ErrorState error={history.error} onRetry={() => void history.refetch()} />}
      {history.data?.partial && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('eng:history.partial')}
        </Alert>
      )}
      {history.data && history.data.items.length === 0 && <EmptyState titleKey="eng:history.empty" />}
      {history.data && history.data.items.length > 0 && (
        <List dense aria-label={t('eng:history.title')}>
          {history.data.items.map((item) => (
            <ListItem key={`${item.kind}-${item.id}`} disableGutters data-testid={`history-${item.kind}`}>
              <ListItemText
                primary={
                  <>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t(`eng:history.kind.${item.kind}`, { defaultValue: item.kind })}
                      sx={{ marginInlineEnd: 1 }}
                    />
                    {t(item.titleKey, {
                      defaultValue: t(`eng:history.kind.${item.kind}`, { defaultValue: item.kind }),
                    })}
                  </>
                }
                secondary={
                  <>
                    <JalaliDate value={item.at} withTime />
                    {historyDetail(item, t) ? ` · ${historyDetail(item, t)}` : ''}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
}
